import _isArray from 'lodash/isArray';
import _concat from 'lodash/concat';
import _every from 'lodash/every';
import _filter from 'lodash/filter';
import _forEach from 'lodash/forEach';
import _isEmpty from 'lodash/isEmpty';
import _includes from 'lodash/includes';
import _intersection from 'lodash/intersection';
import _orderBy from 'lodash/orderBy';
import _size from 'lodash/size';
import _some from 'lodash/some';
import _split from 'lodash/split';
import _values from 'lodash/values';
import _flatMap from 'lodash/flatMap';
import _uniq from 'lodash/uniq';
// Serialization
import { deserialize } from 'serializr';
import { permissionSchema } from './schemas';
// Permissions
import permissions from '../constants/Permissions';

const {
  pCpm,
  pAccountsManagement,
  pBooking,
  pAdmin,
  pFeature,
  pSupport,
} = permissions;
const cpmPermissions = _values(pCpm);
const accountsPermissions = _values(pAccountsManagement);
const bookingPermissions = _values(pBooking);
const supportPermissions = _values(pSupport);

/**
 * Serialization
 */
const deserializePermission = (data) => deserialize(permissionSchema, data);

/**
 * Lists
 */
const permissionsGroups = [
  { name: 'installations', targets: ['installation'] },
  { name: 'hardware', targets: ['device'] },
  { name: 'accounts', targets: ['user', 'company'] },
  { name: 'admin', targets: ['admin'] },
  { name: 'callback', targets: ['callback'] },
  { name: 'cpm', targets: ['cpm'] },
  { name: 'accountsManagement', targets: ['account'] },
  { name: 'booking', targets: ['booking'] },
  { name: 'support', targets: ['support'] },
];

/**
 * Utils
 */
const sortPermissions = (permissionsList, fields = ['id'], dirs = ['asc']) => _orderBy(permissionsList, fields, dirs);

const filterPermissionsFromTarget = (
  permissionsList,
  target,
) => _filter(permissionsList, (permission) => {
  if (target === 'cpm') {
    return _includes(cpmPermissions, permission.name);
  }
  if (target === 'account') {
    return _includes(accountsPermissions, permission.name);
  }
  if (target === 'booking') {
    return _includes(bookingPermissions, permission.name);
  }
  if (target === 'support') {
    return _includes(supportPermissions, permission.name);
  }
  let searchKey = `${target}_`;
  // Special targets (no _read or _write)
  if (target === 'admin') {
    searchKey = 'admin';
  } else if (target === 'callback') {
    searchKey = 'callback';
  }
  return _includes(permission.name, searchKey);
});

const filterPermissionsGroupsFromPermissions = (
  permissionsList,
) => _filter(permissionsGroups, ({ targets }) => {
  let permissionsGroupVisible = false;
  _forEach(targets, (target) => {
    const filteredPermissions = filterPermissionsFromTarget(
      permissionsList,
      target,
      cpmPermissions,
    );
    if (_size(filteredPermissions) > 0) {
      permissionsGroupVisible = true;
      return false;
    }
  });
  return permissionsGroupVisible;
});

const getUniquePermissionsByCompanyId = (arr, companyId) => {
  const filteredData = _filter(arr, { company_id: companyId });
  const permissionsArray = _flatMap(filteredData, 'permissions');

  return _uniq(permissionsArray);
};

const getAllUserPermissions = (arr) => {
  const permissionsArray = _flatMap(arr, 'permissions');
  return _uniq(permissionsArray);
};

const isAllowed = (permissions, userContext) => { // eslint-disable-line no-shadow
  const { needed, permissionsOrContext } = permissions;
  let allowed = false;

  // Check `isAdmin` flag
  const { isAdmin } = userContext;
  if (isAdmin) {
    return true;
  }

  if (!permissionsOrContext) {
    return false;
  }

  // Get the user permissions to be used
  let userPermissionsOrFeatures = null;

  // permissionsOrContext can be:
  // an array of permissions like ['p1', 'p2', 'p3']
  // a context to be matched with userContext object
  if (_isArray(permissionsOrContext)) {
    userPermissionsOrFeatures = permissionsOrContext;
  } else {
    userPermissionsOrFeatures = userContext[permissionsOrContext];
  }

  // Now check if user has needed permission
  const allNeeded = _split(needed, '&&'); // Permissions separated by '&&' => 'user_read&&user_write'
  const oneNeeded = _concat(_split(needed, '||'), []); // Permissions separated by '||' => 'user_read||user_write'

  if (_size(allNeeded) > 1) {
    // All permissions must be present
    allowed = _every(allNeeded, (p) => _includes(userPermissionsOrFeatures, p));
  } else {
    // At least one permission must be present
    allowed = !_isEmpty(_intersection(userPermissionsOrFeatures, oneNeeded));
  }

  return allowed;
};

const isAdmin = (featuresOrPermissions, useGraphQL) => {
  let result = false;

  if (useGraphQL) {
    // We are in CPM product, check if `pFeature.admin` is present in `featuresOrPermissions`
    result = _some(featuresOrPermissions, (item) => _includes(item, pFeature.admin));
  } else {
    // We are in OPAC product, try to find `pAdmin` in one of the `permissions` array
    result = _some(featuresOrPermissions, (item) => _includes(item.permissions, pAdmin));
  }

  return result;
};

const hasOnlyBookingsPermission = (permissionsToCheck) => {
  const haveSameLength = permissionsToCheck.length >= 1 && permissionsToCheck.length === bookingPermissions.length;
  /* Assuming there are no duplicates */
  const haveSameElements = permissionsToCheck.every((permission) => bookingPermissions.includes(permission));
  return haveSameLength && haveSameElements;
};

/**
 * Model
 */
const permission = {
  // Name
  name: 'permission',

  // Serialization
  deserialize: deserializePermission,

  // Sort elements
  sortElements: sortPermissions,

  // Lists
  groups: permissionsGroups,

  // Utils
  filterFromTarget: filterPermissionsFromTarget,
  filterGroupsFromPermissions: filterPermissionsGroupsFromPermissions,
  isAllowed,
  isAdmin,
  getUniquePermissionsByCompanyId,
  getAllUserPermissions,
  hasOnlyBookingsPermission,
};

module.exports = permission;



// WEBPACK FOOTER //
// ./models/permission.js